/**
 * The Agent tab: conversations with an inspector, skills, activity, a guide
 * for staff and (for administrators) agent settings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Activity, Archive, BookOpen, LifeBuoy, MessageSquare, Plus, Settings, Sparkles } from 'lucide-react';
import { useAgent } from './AgentProvider';
import { agentApi } from './agentApi';
import { AGENT_ADMIN_ROLES, promptsForTab } from './agentContextMap';
import { readLocal, timeAgo, writeLocal } from './format';
import { useConversationController } from './useConversationController';
import ActivityFeed from './components/ActivityFeed';
import AgentGuide from './components/AgentGuide';
import AgentSettings from './components/AgentSettings';
import Composer from './components/Composer';
import Conversation from './components/Conversation';
import { EmptyState } from './components/AgentDrawer';
import InspectorPanel from './components/InspectorPanel';
import ProfilePicker from './components/ProfilePicker';
import SkillsManager from './components/SkillsManager';
import StatsStrip from './components/StatsStrip';

export const workspaceSessionKey = (userId) => `dagent.workspaceSession.${userId}`;
export const workspaceSectionKey = (userId) => `dagent.workspaceSection.${userId}`;

const STATUS_LABEL = { RUNNING: 'Working', AWAITING_APPROVAL: 'Needs approval', ERROR: 'Error', IDLE: '' };

function SessionList({ sessions, selectedId, onSelect, onNew, archived, setArchived, onArchive }) {
  return (
    <nav className="dagent-sessions" aria-label="Conversations">
      <div className="dagent-sessions__bar">
        <button type="button" className="dagent-btn dagent-btn--primary dagent-btn--block" onClick={onNew}>
          <Plus size={15} aria-hidden="true" /> New conversation
        </button>
      </div>
      <ul>
        {sessions.map((session) => (
          <li key={session.id}>
            <button type="button" className={`dagent-session ${selectedId === session.id ? 'is-active' : ''}`} onClick={() => onSelect(session.id)}>
              <span className={`dagent-dot dagent-dot--${(session.status || 'IDLE').toLowerCase()}`} aria-hidden="true" />
              <span className="dagent-session__title">{session.title}</span>
              <span className="dagent-session__meta">
                {session.pendingApprovals > 0 ? <span className="dagent-badge dagent-badge--pending_approval">{session.pendingApprovals} to approve</span> : STATUS_LABEL[session.status] || timeAgo(session.updatedAt)}
              </span>
            </button>
            {!archived && selectedId === session.id && (
              <button type="button" className="dagent-session__archive" title="Archive" onClick={() => onArchive(session.id)}>
                <Archive size={13} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
        {sessions.length === 0 && <li className="dagent-muted dagent-pad dagent-small">{archived ? 'No archived conversations.' : 'No conversations yet.'}</li>}
      </ul>
      <label className="dagent-check-all dagent-sessions__foot">
        <input type="checkbox" checked={archived} onChange={(event) => setArchived(event.target.checked)} /> Show archived
      </label>
    </nav>
  );
}

function AgentWorkspace({ user }) {
  const agent = useAgent();
  const isAdmin = AGENT_ADMIN_ROLES.includes(user.role);
  const [section, setSectionState] = useState(() => readLocal(workspaceSectionKey(user.id)) || 'conversations');
  const [sessions, setSessions] = useState([]);
  const [archived, setArchived] = useState(false);
  const [selectedId, setSelectedIdState] = useState(() => readLocal(workspaceSessionKey(user.id)));
  const [statsKey, setStatsKey] = useState(0);

  const setSection = (value) => {
    setSectionState(value);
    writeLocal(workspaceSectionKey(user.id), value);
  };

  const loadSessions = useCallback(async () => {
    try {
      setSessions((await agentApi.listSessions(archived)).sessions);
    } catch (error) {
      // shown by the conversation area when it matters
    }
  }, [archived]);

  const setSelectedId = useCallback(
    (id) => {
      setSelectedIdState(id);
      writeLocal(workspaceSessionKey(user.id), id);
      loadSessions();
    },
    [user.id, loadSessions],
  );

  useEffect(() => {
    loadSessions();
    const timer = setInterval(loadSessions, 8000);
    return () => clearInterval(timer);
  }, [loadSessions]);

  const controller = useConversationController({ sessionId: selectedId, setSessionId: setSelectedId, originTab: 'agent' });
  const status = controller.agentSession.session?.status;

  useEffect(() => {
    if (!status) return;
    loadSessions();
    if (status === 'IDLE') setStatsKey((key) => key + 1);
  }, [status, loadSessions]);

  if (!agent?.enabled) return <p className="dagent-muted">Your role cannot use the agent.</p>;

  const archive = async (id) => {
    try {
      await agentApi.updateSession(id, { archived: true });
      if (id === selectedId) setSelectedId(null);
      loadSessions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // A skill or a guide example starts a new conversation with the request typed in.
  const startWithPrompt = (prompt) => {
    setSelectedId(null);
    agent.setDraft(prompt);
    setSection('conversations');
  };

  const sections = [
    ['conversations', 'Conversations', MessageSquare],
    ['skills', 'Skills', BookOpen],
    ['activity', 'Activity', Activity],
    ['guide', 'Guide', LifeBuoy],
    ...(isAdmin ? [['settings', 'Settings', Settings]] : []),
  ];
  const isEmpty = controller.agentSession.events.length === 0;

  return (
    <div className="dagent-workspace">
      <header className="dagent-workspace__top">
        <div className="dagent-brand dagent-brand--large">
          <span className="dagent-brand__mark">
            <Sparkles size={20} aria-hidden="true" />
          </span>
          <div>
            <div className="dagent-kicker">Operations agent</div>
            <h2 className="dagent-brand__title">Dridh Agent</h2>
          </div>
        </div>
        <StatsStrip scope={isAdmin ? 'all' : undefined} refreshKey={statsKey} />
      </header>

      <div className="dagent-subnav" role="tablist">
        {sections.map(([id, label, Icon]) => (
          <button key={id} type="button" role="tab" aria-selected={section === id} className={`brand-tab ${section === id ? 'brand-tab--active' : ''}`} onClick={() => setSection(id)}>
            <Icon size={14} aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      {section === 'conversations' && (
        <div className="dagent-workspace__grid">
          <SessionList
            sessions={sessions}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onNew={() => setSelectedId(null)}
            archived={archived}
            setArchived={setArchived}
            onArchive={archive}
          />
          <section className="dagent-workspace__chat">
            <div className="dagent-chat-head">
              <div className="dagent-chat-head__title">
                <h3>{controller.agentSession.session?.title || 'New conversation'}</h3>
                {controller.readOnly && <span className="dagent-badge dagent-badge--muted">Read only</span>}
                {status && STATUS_LABEL[status] && <span className={`dagent-badge dagent-badge--${status.toLowerCase()}`}>{STATUS_LABEL[status]}</span>}
              </div>
              <ProfilePicker onConfigure={() => setSection('settings')} />
            </div>
            <Conversation controller={controller} emptyState={<EmptyState />} />
            <Composer
              disabled={!agent.meta?.profiles?.length || controller.readOnly}
              running={controller.running}
              onSend={controller.send}
              onStop={controller.stop}
              suggestions={isEmpty ? promptsForTab('default') : []}
              draft={agent.draft}
              clearDraft={() => agent.setDraft('')}
              placeholder={
                !agent.meta?.profiles?.length
                  ? isAdmin
                    ? 'Add an agent in Settings to start'
                    : 'Ask an administrator to add an agent'
                  : controller.awaiting
                    ? 'Approve above, or send a new message to skip'
                    : undefined
              }
            />
          </section>
          <InspectorPanel controller={controller} pageContext={agent.pageContext} />
        </div>
      )}

      {section === 'skills' && <SkillsManager user={user} toolNames={(agent.meta?.tools || []).map((tool) => tool.name)} onUseSkill={startWithPrompt} />}
      {section === 'activity' && <ActivityFeed user={user} />}
      {section === 'guide' && <AgentGuide onTryPrompt={startWithPrompt} onOpenSection={setSection} />}
      {section === 'settings' && isAdmin && <AgentSettings onChanged={agent.refreshMeta} />}
    </div>
  );
}

export default AgentWorkspace;
